import React from "react";
import {
    QueryClient,
    UseMutationResult,
    UseQueryResult,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { AuthContext } from "./context/auth-context";
import {
    Meeting,
    ResourceType,
    Organisation,
    Patient,
    User,
    Client,
    MeetingEvent,
    MeetingSurvey,
    UserType,
    UserJoinInformation,
    SubstituteResult,
    MeetingJoinType,
    ProposedTimeSlot,
    CalendarSlot,
    ConflictTimeslot,
    MeetingUrlInformation,
    Timeslot,
    MeetingConfirmationInfo,
    ActiveStatus,
    CanSubstituteData,
    PatientSummary,
} from "./types";

import MeetingsAPIService from "./services/meeting-service";

import UsersServiceAPI, { RemoveUserOutput } from "./services/users-service";
import OrganisationAPIService from "./services/organisation-service";
import PatientService from "./services/patient-service";
import ClientAPIService from "./services/client-service";

import { uploadFileAndCreateDownloadURL } from "./services/upload-service";
import { joinMeeting, JoinResponse } from "./services/chime-service";
import { MeetingManager } from "amazon-chime-sdk-component-library-react";
import { getSubscribeUrl } from "./services/calendar-service";
import { addMeetingToListMeetingQueries, addQueryDataToList, patchQueryData, updateQueryData } from "./services/store-utils";
import { PutMeetingValues, MeetingUpdateValues, OrganisationInput, OrganisationUpdateValues, ClientUpdateValues, UpdateMembershipValues, UserUpdateValues, UserInput, ClientValues } from "../../input-types";

export interface Message {
    message: string,
    type: "error" | "warning" | "success"
}

export interface MeetingCodeInput {
    meetingId: string,
    userId: string,
    joinCode: string,
}

//////////// Meetings ////////////
export const useListMeeting = (
    startRange?: Date,
    endRange?: Date,
    clientId?: string | undefined  // for admins to specify client id
): UseQueryResult<CalendarSlot[]> => {

    const {
        tryGetStoredSession,
        clientId: authClientId,
    } = React.useContext(AuthContext);

    const finalClientId = clientId != undefined ? clientId : authClientId

    const startKey = startRange?.toISOString()
    const endKey = endRange?.toISOString()

    return useQuery({
        queryKey: [ResourceType.Meeting, finalClientId, startKey, endKey],
        queryFn: async (): Promise<CalendarSlot[]> => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.listMeetings(authToken, finalClientId, startRange, endRange)
        },
    });
};

export const useListMeetingDay = (
    date: Date,
    clientId?: string | undefined  // for admins to specify client id
): UseQueryResult<CalendarSlot[]> => {

    const {
        tryGetStoredSession,
        clientId: authClientId,
    } = React.useContext(AuthContext);

    const finalClientId = clientId != undefined ? clientId : authClientId

    const startRange = new Date(date);
    startRange.setDate(date.getDate() - 32);
    startRange.setHours(0, 0, 0, 0);

    const endRange = new Date(date);
    endRange.setDate(date.getDate() + 32);
    endRange.setHours(0, 0, 0, 0);

    const startKey = startRange?.toISOString()
    const endKey = endRange?.toISOString()

    return useQuery({
        queryKey: [ResourceType.Meeting, finalClientId, startKey, endKey],
        queryFn: async (): Promise<CalendarSlot[]> => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.listMeetings(authToken, finalClientId, startRange, endRange)
        },
    })
};

export const useGetMeeting = (
    meetingId: string,
    clientId?: string | undefined,
): UseQueryResult<Meeting | null> => {

    const {
        tryGetStoredSession,
        clientId: authClientId
    } = React.useContext(AuthContext);

    const finalClientId = clientId != undefined ? clientId : authClientId

    return useQuery({
        queryKey: [ResourceType.Meeting, meetingId, finalClientId],
        queryFn: async (): Promise<Meeting | null> => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.getMeeting(authToken, meetingId, finalClientId)
        },
    });
}

export const useGetMeetingsWithIds = (
    meetingIds: string[],
    clientId?: string | undefined,
): UseQueryResult<(Meeting | null)[]> => {

    const {
        tryGetStoredSession,
        clientId: authClientId
    } = React.useContext(AuthContext)

    const finalClientId = clientId != undefined ? clientId : authClientId

    return useQuery({
        queryKey: [ResourceType.Meeting, meetingIds, finalClientId],
        queryFn: async (): Promise<(Meeting | null)[]> => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.getMeetingsWithIds(authToken, meetingIds, finalClientId)
        },
    });
}

export const useGetMeetingEvents = (
    meetingIds: string[],
    clientId?: string | undefined,
): UseQueryResult<{
    meetingId: string,
    events: MeetingEvent[]
}[]> => {

    const {
        tryGetStoredSession,
        clientId: authClientId
    } = React.useContext(AuthContext);

    const finalClientId = clientId != undefined ? clientId : authClientId

    return useQuery({
        queryKey: [ResourceType.Meeting, meetingIds, "events", finalClientId],
        queryFn: async (): Promise<{
            meetingId: string,
            events: MeetingEvent[]
        }[]> => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.getMeetingEvents(authToken, meetingIds, finalClientId)
        },
    });
}

export const useAddMeeting = (
    onSuccess?: (meeting: Meeting | null, patient: Patient | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<{ meeting: Meeting | null, patient: Patient | null } | Error | null, any, PutMeetingValues, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (values: PutMeetingValues) => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.addMeeting(authToken, values, clientId);
        },
        onSuccess: (data: { meeting: Meeting | null, patient: Patient | null }) => {
            addMeetingToListMeetingQueries(queryClient, data?.meeting)

            updateQueryData(queryClient, data?.patient, ResourceType.Patient, "patientId")

            if (onSuccess) {
                onSuccess(data.meeting, data.patient);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
};

export const useAddTrainingMeeting = (
    onSuccess?: (meeting: Meeting | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<Meeting | Error | null, any, void, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    return useMutation({
        mutationFn: async () => {
            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.addTrainingMeeting(authToken, clientId);
        },
        onSuccess: (data: Meeting | null) => {
            // Training meetings are not included in other fetches so no data needs to change
            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
}

export const useCancelMeeting = (
    onSuccess?: (meeting: Meeting | null, patient: Patient | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<{ meeting: Meeting | null, patient: Patient | null }, any, { meetingId: string }, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: { meetingId: string }) => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.cancelMeeting(authToken, input.meetingId, clientId);
        },
        onSuccess: (data: { meeting: Meeting | null, patient: Patient | null }) => {

            if (!data.meeting || !data.patient) {
                if (onSuccess) {
                    onSuccess(null, null)
                }
                return
            }

            updateQueryData(queryClient,
                data?.meeting,
                ResourceType.Meeting,
                "meetingId",
                ["join", "events", "confirm", "urls"]
            )
            updateQueryData(queryClient, data?.patient, ResourceType.Patient, "patientId")

            if (onSuccess) {
                onSuccess(data.meeting, data.patient);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    })
};

export const useUpdateMeeting = (
    onSuccess?: (meeting: Meeting | null, patient: Patient | null) => void,
    onError?: (err: any) => void
): UseMutationResult<{ meeting: Meeting | null, patient: Patient | null }, any, { values: MeetingUpdateValues, meetingId: string }, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: { values: MeetingUpdateValues, meetingId: string }) => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.updateMeeting(authToken, input.meetingId, input.values, clientId);
        },
        onSuccess: (data: { meeting: Meeting | null, patient: Patient | null }) => {
            if (!data.meeting || !data.patient) {
                if (onSuccess) {
                    onSuccess(null, null)
                }
                return
            }

            updateQueryData(queryClient,
                data.meeting,
                ResourceType.Meeting,
                "meetingId",
                ["join", "events", "confirm", "urls"]
            )
            updateQueryData(queryClient, data.patient, ResourceType.Patient, "patientId")

            if (onSuccess) {
                onSuccess(data.meeting, data.patient);
            }
        },
        onError: (error) => {
            if (onError) {
                onError(error);
            }
        },
    });
};

interface SubstituteInput extends MeetingCodeInput {
    emails: string[]
}

export const useAddSubstituteAttendee = (
    onSuccess?: (message: string, substituteResult: SubstituteResult[]) => void,
    onError?: (err: any) => void
): UseMutationResult<{
    message: string,
    substituteResult: SubstituteResult[],
}, any, SubstituteInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (values: SubstituteInput) => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.addSubstituteAttendee({
                meetingId: values.meetingId,
                userId: values.userId,
                joinCode: values.joinCode,
                authToken,
                clientId,
            }, values.emails);
        },
        onSuccess: (
            data: {
                message: string,
                substituteResult: SubstituteResult[],
            },
            variables: SubstituteInput
        ) => {

            queryClient.invalidateQueries({ queryKey: [ResourceType.Meeting, variables?.meetingId] });

            if (onSuccess) {
                onSuccess(data?.message, data?.substituteResult);
            }
        },
        onError: (error) => {
            if (onError) {
                onError(error);
            }
        },
    });
};

export const useCanSubstituteAttendee = (
    meetingId: string,
    userId: string,
    joinCode: string,
): UseQueryResult<CanSubstituteData> => {

    const {
        tryGetStoredSession,
        clientId,
    } = React.useContext(AuthContext);

    return useQuery({
        queryKey: [ResourceType.Meeting, meetingId, "invite"],
        queryFn: async (): Promise<CanSubstituteData> => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.canSubstituteAttendee({
                meetingId,
                userId,
                joinCode,
                authToken,
                clientId
            })
        },
    });
}

export const useRequestSubstituteLink = (
    onSuccess?: (message: Message) => void,
    onError?: (err: any) => void
): UseMutationResult<Message, any, MeetingCodeInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    return useMutation({
        mutationFn: async (values: MeetingCodeInput) => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.requestSubstituteLink({
                meetingId: values.meetingId,
                userId: values.userId,
                joinCode: values.joinCode,
                authToken,
                clientId,
            })
        },
        onSuccess: (data: Message) => {
            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            if (onError) {
                onError(error);
            }
        },
    });
};

interface SurveyInput extends MeetingCodeInput {
    survey: MeetingSurvey
}

export const useUpdateMeetingSurvey = (
    onSuccess?: (survey: MeetingSurvey | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<MeetingSurvey | null, any, SurveyInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    return useMutation({
        mutationFn: async (values: any) => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.updateMeetingSurvey(
                {
                    meetingId: values.meetingId,
                    userId: values.userId,
                    joinCode: values.joinCode,
                    authToken,
                    clientId,
                }, values.survey)
        },
        onSuccess: (data) => {
            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
}

interface AttendanceInput extends MeetingCodeInput {
    accept: boolean
}

export const useUpdateMeetingAttendance = (
    onSuccess?: (accept: boolean) => void,
    onFailure?: (err: any) => void
): UseMutationResult<{ accept: boolean } | null, any, AttendanceInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId,
    } = React.useContext(AuthContext);

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values: AttendanceInput) => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.updateMeetingAttendance({
                meetingId: values.meetingId,
                userId: values.userId,
                joinCode: values.joinCode,
                authToken,
                clientId,
            }, values.accept)
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [ResourceType.Meeting, variables?.meetingId, "confirm"] });

            if (onSuccess) {
                onSuccess(Boolean(data?.accept));
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
}

export const useGetMeetingJoinInfo = (
    meetingId: string,
    userId: string,
    joinCode: string,
): UseQueryResult<UserJoinInformation | null> => {

    const {
        tryGetStoredSession,
        clientId,
    } = React.useContext(AuthContext);

    return useQuery({
        queryKey: [ResourceType.Meeting, meetingId, "join"],
        queryFn: async (): Promise<UserJoinInformation | null> => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.getMeetingJoinInfo({
                meetingId,
                userId,
                joinCode,
                authToken,
                clientId
            })
        },
    });
}

export const useGetMeetingAttendance = (
    meetingId: string,
    userId: string,
    joinCode: string,
): UseQueryResult<MeetingConfirmationInfo | null> => {

    const {
        tryGetStoredSession,
        clientId,
    } = React.useContext(AuthContext);

    return useQuery({
        queryKey: [ResourceType.Meeting, meetingId, "confirmation"],
        queryFn: async (): Promise<MeetingConfirmationInfo | null> => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.getMeetingAttendance({
                meetingId,
                userId,
                joinCode,
                authToken,
                clientId
            })
        },
    });
}

export const useGetMeetingUrls = (
    meetingId: string,
    userId: string,
    joinCode: string,
): UseQueryResult<MeetingUrlInformation | null> => {

    const {
        tryGetStoredSession,
        clientId,
    } = React.useContext(AuthContext);

    return useQuery({
        queryKey: [ResourceType.Meeting, meetingId, "urls"],
        queryFn: async (): Promise<MeetingUrlInformation | null> => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.getMeetingUrls({
                meetingId,
                userId,
                joinCode,
                authToken,
                clientId
            })
        },
    });
}

interface UpdateAttendeeInput extends MeetingCodeInput {
    user: UserUpdateValues
}

export const useUpdateMeetingAttendee = (
    onSuccess?: (user: User | null) => void,
    onError?: (err: any) => void
): UseMutationResult<User | null, any, UpdateAttendeeInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (values: UpdateAttendeeInput) => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.updateMeetingAttendee(
                {
                    meetingId: values.meetingId,
                    userId: values.userId,
                    joinCode: values.joinCode,
                    authToken,
                    clientId,
                }, values.user)
        },
        onSuccess: (data: User | null, variables: UpdateAttendeeInput) => {
            queryClient.invalidateQueries({ queryKey: [ResourceType.Meeting, variables.meetingId, "join"] })
            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            if (onError) {
                onError(error);
            }
        },
    });
};

export const useGetScheduleData = (
    userIds: string[],
    startTime: Date,
    endTime: Date,
    patientId?: string | undefined,
    clientId?: string | undefined,
): UseQueryResult<ConflictTimeslot[]> => {

    const {
        tryGetStoredSession,
        clientId: authClientId,
    } = React.useContext(AuthContext)

    const startKey = startTime?.toISOString()
    const endKey = endTime?.toISOString()

    const finalClientId = clientId != undefined ? clientId : authClientId;

    return useQuery({
        queryKey: [ResourceType.Meeting, "schedule", finalClientId, userIds, startKey, endKey, patientId],
        queryFn: async (): Promise<ConflictTimeslot[]> => {

            const authToken = await tryGetStoredSession()

            const items = await MeetingsAPIService.listScheduleData(authToken, startTime, endTime, userIds, patientId, clientId)

            return items.map(item => {
                return {
                    startTime: item.startTime,
                    endTime: item.endTime,
                    conflictingUsers: item.attendeeIds.filter(item => userIds.includes(item)),
                    patientConflict: item.patientId == patientId
                }
            })
        },
    });
}

export const useGetMeetingProposalData = (
    meetingId: string,
    userId: string,
    joinCode: string,
    startTime: Date,
    endTime: Date,
): UseQueryResult<CalendarSlot[]> => {

    const {
        tryGetStoredSession,
        clientId,
    } = React.useContext(AuthContext)

    return useQuery({
        queryKey: [ResourceType.Meeting, meetingId, "proposal"],
        queryFn: async (): Promise<CalendarSlot[]> => {

            const authToken = await tryGetStoredSession()

            return await MeetingsAPIService.getMeetingScheduleData({
                meetingId,
                userId,
                joinCode,
                authToken,
                clientId
            },
                startTime,
                endTime,
            )
        },
    });
}

export const useGetProposedMeetingTimes = (
    meetingId: string,
    userId: string,
    joinCode: string,
): UseQueryResult<{ meeting: CalendarSlot, proposedTimes: ConflictTimeslot[] } | null> => {

    const {
        tryGetStoredSession,
        clientId,
    } = React.useContext(AuthContext);

    return useQuery({
        queryKey: [ResourceType.Meeting, meetingId, "proposed"],
        queryFn: async (): Promise<{
            meeting: CalendarSlot,
            proposedTimes: ConflictTimeslot[]
        } | null> => {

            const authToken = await tryGetStoredSession();

            const result = await MeetingsAPIService.getProposedMeetingTimes({
                meetingId,
                userId,
                joinCode,
                authToken,
                clientId
            })

            if (!result) {
                return null
            }

            return result
        },
    });
}

interface UpdateProposedTimesInput extends MeetingCodeInput {
    proposedTimes: Timeslot[]
}

export const useUpdateProposedTimes = (
    onSuccess?: (message: string, result: ProposedTimeSlot[] | undefined) => void,
    onError?: (err: any) => void
): UseMutationResult<{
    message: string,
    result: ProposedTimeSlot[] | undefined,
}, any, UpdateProposedTimesInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (values: UpdateProposedTimesInput) => {

            const authToken = await tryGetStoredSession();

            return await MeetingsAPIService.updateProposedTimes({
                meetingId: values.meetingId,
                userId: values.userId,
                joinCode: values.joinCode,
                authToken,
                clientId,
            }, values.proposedTimes);
        },
        onSuccess: (
            data: {
                message: string,
                result: ProposedTimeSlot[] | undefined,
            },
            variables: UpdateProposedTimesInput) => {

            queryClient.invalidateQueries({ queryKey: [ResourceType.Meeting, variables?.meetingId] });

            if (onSuccess) {
                onSuccess(data?.message, data?.result);
            }
        },
        onError: (error) => {
            if (onError) {
                onError(error);
            }
        },
    });
};

interface RequestNewProposedTimesInput extends MeetingCodeInput {
    targetId: string
}

export const useRequestNewProposedTimes = (
    onSuccess?: (message: string) => void,
    onError?: (err: any) => void
): UseMutationResult<Message, any, RequestNewProposedTimesInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    return useMutation({
        mutationFn: async (values: RequestNewProposedTimesInput) => {

            const authToken = await tryGetStoredSession();

            return await MeetingsAPIService.requestNewProposedTimes({
                meetingId: values.meetingId,
                userId: values.userId,
                joinCode: values.joinCode,
                authToken,
                clientId,
            }, values.targetId);
        },
        onSuccess: (data: Message) => {

            if (onSuccess) {
                onSuccess(data?.message);
            }
        },
        onError: (error) => {
            if (onError) {
                onError(error);
            }
        },
    });
};

interface ConfirmMeetingTimeInput extends MeetingCodeInput {
    startTime: Date,
    endTime: Date,
}

export const useConfirmMeetingTime = (
    onSuccess?: (message: string, startTime: Date | null, endTime: Date | null) => void,
    onError?: (err: any) => void
): UseMutationResult<{
    message: string,
    startTime: Date | null,
    endTime: Date | null,
}, any, ConfirmMeetingTimeInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (values: ConfirmMeetingTimeInput) => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.confirmMeetingTimes({
                meetingId: values.meetingId,
                userId: values.userId,
                joinCode: values.joinCode,
                authToken,
                clientId,
            }, values.startTime, values.endTime)
        },
        onSuccess: (data: {
            message: string,
            startTime: Date | null,
            endTime: Date | null,
        }, variables: ConfirmMeetingTimeInput) => {

            queryClient.invalidateQueries({ queryKey: [ResourceType.Meeting, variables?.meetingId] });

            if (onSuccess) {
                onSuccess(data?.message, data?.startTime, data?.endTime);
            }
        },
        onError: (error) => {
            if (onError) {
                onError(error);
            }
        },
    });
};

//////////// PhotoUrl ////////////

interface UploadFileInput extends MeetingCodeInput {
    joinType: MeetingJoinType,
    file: File
}

export const useUploadFile = (
    onSuccess?: () => void,
    onFailure?: (err: any) => void
): UseMutationResult<string, any, UploadFileInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId,
    } = React.useContext(AuthContext);

    return useMutation({
        mutationFn: async (values: UploadFileInput) => {

            const authToken = await tryGetStoredSession();

            return uploadFileAndCreateDownloadURL(
                values.file,
                values.joinType,
                {
                    userId: values.userId,
                    meetingId: values.meetingId,
                    joinCode: values.joinCode,
                    authToken,
                    clientId
                },
            );
        },
        onSuccess: () => {
            // Will invalidate cache when iot message is received
            if (onSuccess) {
                onSuccess();
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
};

interface ExportSurveyInput {
    clientId: string | undefined
    startRange?: Date | undefined,
    endRange?: Date | undefined,
}

export const useExportSurvey = (
    onSuccess: (data: string) => void,
    onFailure: (error: any) => void,
): UseMutationResult<string, any, ExportSurveyInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId,
    } = React.useContext(AuthContext);

    return useMutation({
        mutationFn: async (values: ExportSurveyInput) => {

            const authToken = await tryGetStoredSession();

            return MeetingsAPIService.exportMeetingSurvey(
                authToken,
                values.clientId || clientId,
                values.startRange,
                values.endRange)
        },
        onSuccess: (data: string) => {
            onSuccess(data)
        },
        onError: (error) => {
            onFailure(error)
        },
    });
};

//////////// Users ////////////
export const useListUsers = (
    includeDeleted?: boolean,
    clientId?: string | undefined  // for admins to specify client id
): UseQueryResult<User[]> => {

    const {
        tryGetStoredSession,
        clientId: authClientId
    } = React.useContext(AuthContext);

    const finalClientId = clientId != undefined ? clientId : authClientId

    return useQuery({
        queryKey: [ResourceType.User, finalClientId],
        queryFn: async (): Promise<User[]> => {

            const authToken = await tryGetStoredSession();

            return UsersServiceAPI.listUsers(authToken, includeDeleted || false, finalClientId)
        },
    });
}

export const useGetUserWithIds = (
    userIds: string[],
    clientId?: string | undefined  // for admins to specify client id
): UseQueryResult<(User | null)[]> => {

    const {
        tryGetStoredSession,
        clientId: authClientId
    } = React.useContext(AuthContext);

    const finalClientId = clientId != undefined ? clientId : authClientId

    return useQuery({
        queryKey: [ResourceType.User, userIds, finalClientId],
        queryFn: async (): Promise<(User | null)[]> => {

            const authToken = await tryGetStoredSession();

            return UsersServiceAPI.getUserWithIds(authToken, userIds, finalClientId)
        },
    });
}

export const useGetCurrentUser = (): UseQueryResult<User | null> => {

    const { userId } = React.useContext(AuthContext);
    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    // DO NOT ADD enabled: !!userId,
    // Will break first call -> break protected route -> break website
    return useQuery({
        queryKey: [ResourceType.User, userId, clientId],
        queryFn: async (): Promise<User | null> => {

            const authToken = await tryGetStoredSession();

            return UsersServiceAPI.getUser(authToken, userId, clientId)
        },
    });
}

export const useUpdateCurrentUser = (
    onSuccess?: (user: User | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<User | null, any, UserUpdateValues, unknown> => {

    const {
        tryGetStoredSession,
        clientId,
        userId,
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values: UserUpdateValues) => {

            const authToken = await tryGetStoredSession();

            return UsersServiceAPI.updateCurrentUser(authToken, userId, values, clientId);
        },
        onSuccess: (data: User | null) => {
            if (data) {
                updateQueryData(queryClient, data, ResourceType.User, "userId")
            }

            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
}

export const useUpdateUserType = (
    onSuccess?: (user: User | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<User | null, any, { targetId: string, userType: UserType, clientId?: string | undefined }, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values: { targetId: string, userType: UserType, clientId?: string | undefined }) => {

            const authToken = await tryGetStoredSession();

            const finalClientId = values.clientId || clientId

            return UsersServiceAPI.updateUserType(authToken, values.targetId, values.userType, finalClientId);
        },
        onSuccess: (data: User | null) => {
            if (data) {
                updateQueryData(queryClient, data, ResourceType.User, "userId")
            }

            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
}

export const useAddUser = (
    onSuccess?: (user: User | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<User | null, any, UserInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values: UserInput) => {

            const authToken = await tryGetStoredSession();

            return UsersServiceAPI.inviteUser(authToken, values, clientId);
        },
        onSuccess: (data: User | null) => {

            if (data) {
                for (let organisationId of data.organisationIds) {
                    queryClient.invalidateQueries({ queryKey: [ResourceType.Organisation, organisationId] });
                }
            }

            updateQueryData(queryClient, data, ResourceType.User, "userId")

            const usersQueryKey: unknown[] = [ResourceType.User, clientId]
            const cachedUsers = queryClient.getQueryData(usersQueryKey) as User[] | undefined

            if (Array.isArray(cachedUsers) && !cachedUsers.some((item) => item?.userId === data?.userId)) {
                queryClient.setQueryData(usersQueryKey, [...cachedUsers, data])
            }

            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
}

export const useInviteUserViaEmail = (
    onSuccess?: (user: User | null, email: string) => void,
    onFailure?: (err: any) => void
): UseMutationResult<User | null, any, { email: string }, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values: { email: string }) => {

            const authToken = await tryGetStoredSession();

            return UsersServiceAPI.inviteUser(authToken, { email: values.email, userType: UserType.Guest }, clientId);
        },
        onSuccess: (data: User | null, variables: { email: string }) => {

            if (data) {
                addQueryDataToList(queryClient, data, [ResourceType.User, clientId])
            }

            if (onSuccess) {
                onSuccess(data, variables.email);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
}

export const useResendInviteEmail = (
    onSuccess?: () => void,
    onFailure?: (err: any) => void
): UseMutationResult<boolean, any, { email: string, userId: string }, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    return useMutation({
        mutationFn: async (values: { email: string, userId: string }) => {

            const authToken = await tryGetStoredSession();

            return UsersServiceAPI.resendInviteLink(authToken, values, clientId);
        },
        onSuccess: () => {
            if (onSuccess) {
                onSuccess();
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
}

const hasTargetClientIds = (item: any, clientIds: string[]): boolean => {
    if (!item) {
        return false
    }

    if (!item.client) {
        return true
    }

    return clientIds.includes(item.clientId)
}

const updateDataForDeleted = (
    queryClient: QueryClient,
    data: RemoveUserOutput,
    userId: string,
    userType: UserType,
) => {

    if (!data) {
        return
    }

    const user = {
        userId,
        userType,
    }

    patchQueryData(
        queryClient,
        user,
        ResourceType.User,
        "userId",
        undefined,
        (item: any) => hasTargetClientIds(item, data.clientIds)
    )

    if (data.organisationIds) {
        for (let organisationId of data.organisationIds) {
            queryClient.invalidateQueries({ queryKey: [ResourceType.Organisation, organisationId] });
        }
    }
}

export const useRemoveUserFromClient = (
    onSuccess?: () => void,
    onFailure?: (err: any) => void):
    UseMutationResult<RemoveUserOutput | Error, any, { userId: string, clientId: string }, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { userId: string, clientId: string }) => {

            const authToken = await tryGetStoredSession();

            return UsersServiceAPI.removeUserFromClient(authToken, input.userId, UserType.Inactive, input.clientId)
        },
        onSuccess: (data: RemoveUserOutput, input: { userId: string, clientId: string }) => {
            updateDataForDeleted(
                queryClient,
                data,
                input.userId,
                UserType.Inactive)

            if (onSuccess) {
                onSuccess();
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    })
}

export const useBlockUser = (
    onSuccess?: () => void,
    onFailure?: (err: any) => void
): UseMutationResult<RemoveUserOutput | Error, any, { userId: string, clientId: string }, unknown> => {

    const {
        tryGetStoredSession,
        clientId,
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { userId: string, clientId: string }) => {
            const authToken = await tryGetStoredSession();

            return await UsersServiceAPI.removeUserFromClient(authToken, input.userId, UserType.Blocked, input.clientId)
        },
        onSuccess: (data: RemoveUserOutput, input: { userId: string, clientId: string }) => {
            updateDataForDeleted(
                queryClient,
                data,
                input.userId,
                UserType.Blocked)

            if (onSuccess) {
                onSuccess();
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    })
}

export const useUnBlockUser = (
    onSuccess?: () => void,
    onFailure?: (err: any) => void
): UseMutationResult<RemoveUserOutput | Error, any, { userId: string, clientId: string }, unknown> => {

    const {
        tryGetStoredSession,
        clientId,
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { userId: string, clientId: string }) => {

            const authToken = await tryGetStoredSession();

            return UsersServiceAPI.unBlockUser(authToken, input.userId, input.clientId)
        },
        onSuccess: (data: RemoveUserOutput, input: { userId: string, clientId: string }) => {
            updateDataForDeleted(
                queryClient,
                data,
                input.userId,
                UserType.Inactive,
            )

            if (onSuccess) {
                onSuccess();
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    })
}
//////////// Organisation ////////////

export const useAddOrganisation = (
    onSuccess: (organisation: Organisation | null) => void,
    onFailure: (err: any) => void
): UseMutationResult<Organisation | null, any, OrganisationInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values: OrganisationInput) => {

            const authToken = await tryGetStoredSession();

            return OrganisationAPIService.addOrganisation(authToken, values, clientId);
        },
        onSuccess: (data: Organisation | null) => {
            addQueryDataToList(queryClient, data, [ResourceType.Organisation, clientId])
            onSuccess(data);
        },
        onError: (error) => {
            onFailure(error);
        }
    });
}

export const useListOrganisations = (
    clientId?: string | undefined  // for admins to specify client id
): UseQueryResult<Organisation[]> => {

    const {
        tryGetStoredSession,
        clientId: authClientId,
    } = React.useContext(AuthContext);

    const finalClientId = clientId != undefined ? clientId : authClientId

    return useQuery({
        queryKey: [ResourceType.Organisation, finalClientId],
        queryFn: async (): Promise<Organisation[]> => {

            const authToken = await tryGetStoredSession();

            return OrganisationAPIService.listOrganisations(authToken, finalClientId)
        },
    });
}

export const useGetOrganisation = (
    organisationId: string,
    clientId?: string | undefined  // for admins to specify client id
): UseQueryResult<Organisation | null> => {

    const {
        tryGetStoredSession,
        clientId: authClientId
    } = React.useContext(AuthContext);

    const finalClientId = clientId != undefined ? clientId : authClientId

    return useQuery({
        queryKey: [ResourceType.Organisation, organisationId, finalClientId],
        queryFn: async (): Promise<Organisation | null> => {

            const authToken = await tryGetStoredSession();

            return OrganisationAPIService.getOrganisation(authToken, organisationId, finalClientId)
        },
    });
}

export const useUpdateOrganisation = (
    onSuccess?: (organisation: Organisation | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<Organisation | null, any, { input: OrganisationUpdateValues, organisationId: string }, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values: { input: OrganisationUpdateValues, organisationId: string }) => {

            const authToken = await tryGetStoredSession();

            return OrganisationAPIService.updateOrganisation(authToken, values.organisationId, values.input, clientId);
        },
        onSuccess: (data: Organisation | null) => {
            updateQueryData(queryClient, data, ResourceType.Organisation, "organisationId")

            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
}

export const useUpdateOrganisationMembership = (
    onSuccess?: (organisation: Organisation | null, user: User | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<{ organisation: Organisation | null, user: User | null }, any, { input: UpdateMembershipValues, organisationId: string }, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values: { input: UpdateMembershipValues, organisationId: string }) => {

            const authToken = await tryGetStoredSession();

            return OrganisationAPIService.updateOrganisationMembership(authToken, values.organisationId, values.input, clientId)
        },
        onSuccess: (data: { organisation: Organisation | null, user: User | null }) => {
            if (data.organisation) {
                updateQueryData(queryClient, data.organisation, ResourceType.Organisation, "organisationId")
            }

            if (data.user) {
                updateQueryData(queryClient, data.user, ResourceType.User, "userId")
            }

            if (onSuccess) {
                onSuccess(data.organisation, data.user);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
}

export const useDeleteOrganisation = (
    onSuccess?: () => void,
    onFailure?: (err: any) => void
): UseMutationResult<void, any, { organisationId: string, clientId?: string }, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { organisationId: string, clientId?: string }) => {

            const authToken = await tryGetStoredSession();

            const finalClientId = input.clientId || clientId

            return OrganisationAPIService.deleteOrganisation(authToken, input.organisationId, finalClientId);
        },
        onSuccess: (_: void, input: { organisationId: string, clientId?: string }) => {

            const deletedOrganisation = {
                organisationId: input.organisationId,
                type: ActiveStatus.Inactive,
            }

            patchQueryData(queryClient, deletedOrganisation, ResourceType.Organisation, "organisationId")

            if (onSuccess) {
                onSuccess();
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        }
    })
}

export const useGetOrganisationWithIds = (
    organisationIds: string[],
    clientId?: string | undefined  // for admins to specify client id
): UseQueryResult<(Organisation | null)[]> => {

    const {
        tryGetStoredSession,
        clientId: authClientId
    } = React.useContext(AuthContext);

    const finalClientId = clientId != undefined ? clientId : authClientId;

    return useQuery({
        queryKey: [ResourceType.Organisation, organisationIds, finalClientId],
        queryFn: async (): Promise<(Organisation | null)[]> => {

            const authToken = await tryGetStoredSession();

            return OrganisationAPIService.getOrganisationWithIds(authToken, organisationIds, finalClientId)
        },
    });
}


//////////// Patient ////////////

export const useListPatients = (
    clientId?: string | undefined  // for admins to specify client id
): UseQueryResult<PatientSummary[]> => {

    const {
        tryGetStoredSession,
        clientId: authClientId
    } = React.useContext(AuthContext);

    const finalClientId = clientId != undefined ? clientId : authClientId

    return useQuery({
        queryKey: [ResourceType.Patient, finalClientId],
        queryFn: async (): Promise<PatientSummary[]> => {

            const authToken = await tryGetStoredSession();

            return PatientService.listPatients(authToken, finalClientId)
        },
    });
};

export const useGetPatient = (
    patientId: string,
    clientId?: string | undefined  // for admins to specify client id
): UseQueryResult<Patient | null> => {

    const {
        tryGetStoredSession,
        clientId: authClientId
    } = React.useContext(AuthContext);

    const finalClientId = clientId != undefined ? clientId : authClientId;

    return useQuery({
        queryKey: [ResourceType.Patient, patientId, finalClientId],
        queryFn: async (): Promise<Patient | null> => {

            const authToken = await tryGetStoredSession();

            return PatientService.getPatient(authToken, patientId, finalClientId)
        },
    });
}

export const useGetPatientsWithIds = (
    patientIds: string[],
    clientId?: string | undefined  // for admins to specify client id
): UseQueryResult<(Patient | null)[]> => {

    const {
        tryGetStoredSession,
        clientId: authClientId
    } = React.useContext(AuthContext);

    const finalClientId = clientId != undefined ? clientId : authClientId;

    return useQuery({
        queryKey: [ResourceType.Patient, patientIds, finalClientId],
        queryFn: async (): Promise<(Patient | null)[]> => {

            const authToken = await tryGetStoredSession();

            return PatientService.getPatientsWithIds(authToken, patientIds, finalClientId)
        },
    });
}

export const useAddPatient = (
    onSuccess?: (patient: Patient | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<Patient | null, unknown, void, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {

            const authToken = await tryGetStoredSession();

            return PatientService.addPatient(authToken, clientId);
        },
        onSuccess: (response: Patient | null) => {
            addQueryDataToList(queryClient, response, [ResourceType.Patient, clientId])

            if (onSuccess) {
                onSuccess(response);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
};

export const useUpdatePatientStatus = (
    onSuccess?: (patient: Patient | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<Patient | null, unknown, { patientId: string, status: ActiveStatus }, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ patientId, status }: { patientId: string, status: ActiveStatus }) => {

            const authToken = await tryGetStoredSession();

            return PatientService.updatePatientStatus(authToken, patientId, status, clientId);
        },
        onSuccess: (response: Patient | null) => {
            if (response) {
                patchQueryData(
                    queryClient,
                    { patientId: response.patientId, status: response.status },
                    ResourceType.Patient,
                    "patientId"
                )

                queryClient.invalidateQueries({ queryKey: [ResourceType.Patient, clientId] })
                queryClient.invalidateQueries({
                    queryKey: [ResourceType.Patient, response.patientId, clientId]
                })
            }

            if (onSuccess) {
                onSuccess(response);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
};

//////////// Chime ////////////

interface JoinMeetingInput extends MeetingCodeInput {
    meetingType: MeetingJoinType
}

export const useJoinMeeting = (
    meetingManager: MeetingManager,
    onSuccess: (joinInfo: JoinResponse | null) => void,
    onFailure: (err: any) => void
): UseMutationResult<JoinResponse | null, unknown, JoinMeetingInput, unknown> => {

    const {
        tryGetStoredSession,
        clientId,
    } = React.useContext(AuthContext);

    return useMutation({
        mutationFn: async (values: JoinMeetingInput) => {

            if (meetingManager == null) {
                return null;
            }

            const authToken = await tryGetStoredSession();

            return joinMeeting(
                meetingManager,
                {
                    meetingId: values.meetingId,
                    userId: values.userId,
                    joinCode: values.joinCode,
                    authToken,
                    clientId,
                },
                values.meetingType);
        },
        onSuccess: (data: any) => {
            onSuccess(data);
        },
        onError: (error) => {
            onFailure(error);
        },
    });
};

//////////// Clients ////////////

export const useListClients = (): UseQueryResult<Client[]> => {
    const {
        tryGetStoredSession,
    } = React.useContext(AuthContext);

    return useQuery({
        queryKey: [ResourceType.Client],
        queryFn: async (): Promise<Client[]> => {
            const authToken = await tryGetStoredSession();

            return ClientAPIService.listClients(authToken)
        },
    });
};

export const useGetClient = (targetClientId: string): UseQueryResult<Client | null> => {
    const {
        tryGetStoredSession,
    } = React.useContext(AuthContext);

    return useQuery({
        queryKey: [ResourceType.Client, targetClientId],
        queryFn: async (): Promise<Client | null> => {
            const authToken = await tryGetStoredSession();

            return ClientAPIService.getClient(authToken, targetClientId)
        },
    });
};

export const useAddClient = (
    onSuccess?: (client: Client | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<Client | null, any, ClientValues, unknown> => {

    const {
        tryGetStoredSession,
        clientId
    } = React.useContext(AuthContext);

    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (clientData: ClientValues) => {
            const authToken = await tryGetStoredSession();

            return ClientAPIService.addClient(authToken, clientData, clientId);
        },
        onSuccess: (data: Client | null) => {
            addQueryDataToList(queryClient, data, [ResourceType.Client])

            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
};

export const useUpdateClient = (
    onSuccess?: (client: Client | null) => void,
    onFailure?: (err: any) => void
): UseMutationResult<Client | null, any, { input: ClientUpdateValues, clientId: string }, unknown> => {
    const {
        tryGetStoredSession,
    } = React.useContext(AuthContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (clientData: { input: ClientUpdateValues, clientId: string }) => {
            const authToken = await tryGetStoredSession();

            return ClientAPIService.updateClient(authToken, clientData.clientId, clientData.input);
        },
        onSuccess: (data: Client | null) => {
            updateQueryData(queryClient, data, ResourceType.Client, "clientId")
            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
};

export const useGetClientWithIds = (
    clientIds: string[]
): UseQueryResult<(Client | null)[]> => {

    const {
        tryGetStoredSession,
    } = React.useContext(AuthContext);

    return useQuery({
        queryKey: [ResourceType.Client, clientIds],
        queryFn: async (): Promise<(Client | null)[]> => {

            const authToken = await tryGetStoredSession();

            return ClientAPIService.getClientsWithIds(authToken, clientIds)
        },
    });
}


//////////// Calendar ////////////

export const useGenerateCalendarSubscriptionUrl = (
    onSuccess?: (url: string) => void,
    onFailure?: (err: any) => void
): UseMutationResult<string | null, unknown, void, unknown> => {
    const {
        tryGetStoredSession,
        userId,
        clientId
    } = React.useContext(AuthContext);

    return useMutation({
        mutationFn: async () => {
            const authToken = await tryGetStoredSession();

            const result = await getSubscribeUrl(authToken, userId, clientId);
            return result?.url ?? null;
        },
        onSuccess: (data) => {
            if (onSuccess) {
                onSuccess(data || "");
            }
        },
        onError: (error) => {
            if (onFailure) {
                onFailure(error);
            }
        },
    });
};